import { useState } from 'react';
import { NAV, totalBreakdown } from '../lib/adminNav';

// Red badges are white on --feedback-error (4.83:1). Amber ones are NOT white on
// --feedback-warning (3.19:1, under the 4.5:1 small text needs); they take the
// warning ink on the warning background (6.37:1), as the NOT WIRED chip does.
const badgeColors = (c) => (c === 'red'
  ? { background: 'var(--feedback-error)', color: 'var(--brand-white)' }
  : { background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)' });

function SidebarContent({ active, onNav, badges, adminName, isMobile }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      {/* Logo — desktop only, mobile has top bar in AdminPortal */}
 
      {/* Mobile top spacing */}
      {isMobile && <div style={{ height: 16, flexShrink: 0 }} />}

      {/* Nav groups */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {NAV.map(group => (
          <div key={group.group} style={{ padding: '8px 10px' }}>
            {group.group && (
              <p style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 1, color: '#c4b8a8', textTransform: 'uppercase', padding: '0 8px', margin: '0 0 4px' }}>
                {group.group}
              </p>
            )}
            {group.items.map(item => {
              const isActive   = active === item.id;
              const badgeCount = item.badgeKey ? (badges[item.badgeKey] || 0) : 0;
              return (
                <button
                  key={item.id}
                  onClick={() => onNav(item.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9,
                    width: '100%', padding: '9px 10px',
                    minHeight: isMobile ? 44 : undefined,
                    borderRadius: 9, border: 'none',
                    background: isActive ? '#FBF2E9' : 'transparent',
                    cursor: 'pointer',
                    marginBottom: 1, opacity: item.soon && !isActive ? 0.6 : 1,
                    textAlign: 'left', fontFamily: 'var(--font-body)',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f5f0e8'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: 'var(--font-size-md)', width: 18, flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ flex: 1, fontSize: 'var(--font-size-base)', fontWeight: isActive ? 700 : 500, color: isActive ? 'var(--brand-rust)' : 'var(--neutral-600)' }}>
                    {item.label}
                  </span>
                  {item.soon && (
                    <span style={{ fontSize: 7.5, fontWeight: 700, color: 'var(--neutral-400)', background: 'var(--brand-cream)', padding: '2px 6px', borderRadius: 6 }}>SOON</span>
                  )}
                  {!item.soon && badgeCount > 0 && (
                    <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, padding: '1px 7px', borderRadius: 'var(--radius-md)', ...badgeColors(item.badgeColor) }}>
                      {badgeCount}
                    </span>
                  )}
                </button>
              );
            })}
            <div style={{ height: 1, background: 'var(--brand-cream)', margin: '6px 4px' }} />
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: '14px 16px', borderTop: '1px solid var(--brand-cream)', display: 'flex', alignItems: 'center', gap: 'var(--space-10)', flexShrink: 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-circle)', background: 'var(--neutral-800)', color: 'var(--feedback-warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 700, fontFamily: 'var(--font-display)', flexShrink: 0 }}>
          {adminName[0]?.toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)' }}>{adminName}</div>
          <div style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)' }}>Super admin</div>
        </div>
      </div>
    </div>
  );
}

// Narrow screens: a full-screen, searchable menu (opened by More in the bottom dock),
// one card per group, 54px rows. Escape and Close both dismiss it.
function MenuSheet({ active, onNav, badges, onClose }) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const groups = NAV
    .map(g => ({ ...g, items: g.items.filter(i => !needle || i.label.toLowerCase().includes(needle)) }))
    .filter(g => g.items.length);
  return (
    <div style={{ position: 'fixed', top: 64, left: 0, right: 0, bottom: 0, zIndex: 9999, background: 'var(--brand-paper)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexShrink: 0, background: 'var(--brand-white)', borderBottom: '1px solid var(--brand-sand)', padding: '12px 16px', display: 'flex', gap: 11, alignItems: 'center' }}>
        <input
          value={q} onChange={e => setQ(e.target.value)} placeholder="Search sections" aria-label="Search sections"
          style={{ flex: 1, minWidth: 0, minHeight: 48, padding: '0 14px', borderRadius: 12, border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: 'var(--neutral-800)', fontSize: 16, fontFamily: 'var(--font-body)' }}
        />
        <button onClick={onClose} style={{ minHeight: 48, padding: '0 16px', borderRadius: 12, border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: 'var(--neutral-800)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Close</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 24px' }}>
        {/* The tip for the total on More: what it counts. Users and Authors sit inside Reports and Approval Queue, so the rows below add up to more than this. */}
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 14px', lineHeight: 1.5 }}>{totalBreakdown(badges)}</p>
        {groups.length === 0 && (
          <p style={{ textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', padding: '40px 0' }}>Nothing matches. Try a different word.</p>
        )}
        {groups.map((g, gi) => (
          <div key={gi} style={{ marginBottom: 20 }}>
            {g.group && (
              <p style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 1.1, color: 'var(--neutral-300)', textTransform: 'uppercase', margin: '0 0 8px' }}>{g.group}</p>
            )}
            <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 14, padding: '2px 14px' }}>
              {g.items.map((it, i) => {
                const on = active === it.id;
                const n = it.badgeKey ? (badges[it.badgeKey] || 0) : 0;
                return (
                  <button
                    key={it.id} onClick={() => onNav(it.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 0', minHeight: 54,
                      background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-body)',
                      borderBottom: i < g.items.length - 1 ? '1px solid var(--brand-cream)' : 'none',
                      opacity: it.soon && !on ? 0.6 : 1,
                    }}
                  >
                    <span style={{ fontSize: 'var(--font-size-md)', width: 22, textAlign: 'center', flexShrink: 0 }}>{it.icon}</span>
                    <span style={{ flex: 1, fontSize: 'var(--font-size-md)', fontWeight: on ? 700 : 500, color: on ? 'var(--brand-rust)' : 'var(--neutral-800)' }}>{it.label}</span>
                    {it.soon && <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--neutral-400)', background: 'var(--brand-cream)', padding: '2px 7px', borderRadius: 6 }}>SOON</span>}
                    {!it.soon && n > 0 && (
                      <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, padding: '1px 7px', borderRadius: 'var(--radius-md)', ...badgeColors(it.badgeColor) }}>{n}</span>
                    )}
                    <span style={{ color: 'var(--neutral-300)', fontSize: 16 }}>›</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminSidebar({ active, onNav, badges = {}, adminName = 'Admin', isMobile, sidebarOpen, onClose }) {
  if (isMobile) {
    return sidebarOpen ? <MenuSheet active={active} onNav={onNav} badges={badges} onClose={onClose} /> : null;
  }

  // Desktop — fixed sidebar
  return (
    <div style={{ width: 232, flexShrink: 0, background: 'var(--brand-white)', borderRight: '1px solid var(--brand-sand)', height: '100%' }}>
      <SidebarContent active={active} onNav={onNav} badges={badges} adminName={adminName} isMobile={false} />
    </div>
  );
}
